import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Buildings, Ticket, MapPin, CalendarBlank, Trash, DownloadSimple, ArrowCounterClockwise } from "@phosphor-icons/react";

const STATUS_CLS = {
  tentative: "bg-[#FEF3C7] text-[#B45309]", definite: "bg-[#D1FAE5] text-[#047857]",
  completed: "bg-[#E0F2FE] text-[#0369A1]", cancelled: "bg-[#F3F4F6] text-[#6B7280] line-through",
};
const selCls = "border border-[#E5E7EB] px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

export default function BookingsBoard() {
  const qc = useQueryClient();
  const { data: raw = [], isLoading } = useQuery({ queryKey: ["all-bookings"], queryFn: () => api.allBookings() });
  const { data: deleted = [] } = useQuery({ queryKey: ["deleted-leads"], queryFn: () => api.deletedLeads() });
  const { data: properties = [] } = useQuery({ queryKey: ["my-properties"], queryFn: () => api.myProperties() });
  const [fStatus, setFStatus] = useState("all");
  const [fProp, setFProp] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const restore = useMutation({
    mutationFn: (id) => api.restoreDeletedLead(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["deleted-leads"] }); qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Lead restored"); },
    onError: () => toast.error("Could not restore lead"),
  });

  const bookings = useMemo(() => raw.filter((b) => {
    if (fStatus !== "all" && b.status !== fStatus) return false;
    if (fProp !== "all" && b.hotel_id !== fProp) return false;
    if (fFrom && (!b.check_in || b.check_in < fFrom)) return false;
    if (fTo && (!b.check_in || b.check_in > fTo)) return false;
    return true;
  }), [raw, fStatus, fProp, fFrom, fTo]);

  const exportCsv = () => {
    const cols = ["account_name", "name", "status", "hotel_name", "location", "check_in", "check_out", "rooms", "room_nights", "value"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...bookings.map((b) => cols.map((c) => esc(b[c])).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ratebridge-bookings-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const groups = useMemo(() => {
    const g = {};
    bookings.forEach((b) => {
      const key = b.account_name || "Unassigned";
      (g[key] = g[key] || { name: key, items: [], value: 0, rn: 0 }).items.push(b);
      if (b.status !== "cancelled") { g[key].value += Number(b.value) || 0; g[key].rn += Number(b.room_nights) || 0; }
    });
    return Object.values(g).sort((a, b) => b.value - a.value);
  }, [bookings]);

  const totalValue = bookings.filter((b) => b.status !== "cancelled").reduce((a, b) => a + (Number(b.value) || 0), 0);

  return (
    <div className="space-y-5" data-testid="bookings-board">
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#F3F4F6] px-3 py-2"><div className="overline text-[#4B5563]">Companies</div><div className="font-display font-black text-xl">{groups.length}</div></div>
        <div className="bg-[#F3F4F6] px-3 py-2"><div className="overline text-[#4B5563]">Bookings</div><div className="font-display font-black text-xl">{bookings.length}</div></div>
        <div className="bg-[#D1FAE5] px-3 py-2"><div className="overline text-[#4B5563]">Total value</div><div className="font-display font-black text-xl text-[#047857]">{money(totalValue)}</div></div>
      </div>

      <div className="flex items-end gap-2 flex-wrap border border-[#E5E7EB] p-3" data-testid="bookings-filters">
        <label className="text-xs text-[#4B5563]">Status<select data-testid="bk-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={`${selCls} block mt-0.5`}><option value="all">All</option><option value="tentative">Tentative</option><option value="definite">Definite</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select></label>
        <label className="text-xs text-[#4B5563]">Property<select data-testid="bk-filter-property" value={fProp} onChange={(e) => setFProp(e.target.value)} className={`${selCls} block mt-0.5`}><option value="all">All</option>{properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
        <label className="text-xs text-[#4B5563]">Arrival from<input data-testid="bk-filter-from" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className={`${selCls} block mt-0.5`} /></label>
        <label className="text-xs text-[#4B5563]">to<input data-testid="bk-filter-to" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className={`${selCls} block mt-0.5`} /></label>
        <button data-testid="bk-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 bg-[#111827] text-white px-3 py-1.5 text-sm font-semibold hover:bg-black ml-auto"><DownloadSimple size={15} weight="bold" /> Export CSV</button>
      </div>

      {isLoading && <p className="text-sm text-[#4B5563]">Loading…</p>}
      {!isLoading && groups.length === 0 && <p className="text-sm text-[#4B5563]" data-testid="bookings-board-empty">No bookings yet. Add bookings under any account, or mark a lead as Won.</p>}

      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.name} className="border border-[#111827]" data-testid={`booking-group-${g.name}`}>
            <div className="flex items-center justify-between bg-[#002FA7] text-white px-4 py-2.5">
              <span className="font-display font-bold flex items-center gap-2"><Buildings size={16} weight="bold" /> {g.name}</span>
              <span className="text-sm font-semibold">{g.items.length} booking{g.items.length > 1 ? "s" : ""} · {g.rn} rm-nts · {money(g.value)}</span>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              {g.items.map((b) => (
                <div key={b.id} className="px-4 py-2.5 flex items-start justify-between gap-3" data-testid={`board-booking-${b.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#111827]">{b.name}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${STATUS_CLS[b.status] || STATUS_CLS.tentative}`}>{b.status}</span>
                    </div>
                    <div className="text-sm text-[#4B5563] mt-0.5 flex items-center gap-1 flex-wrap">
                      {b.hotel_name && <span>{b.hotel_name}</span>}
                      {b.location && <span className="text-[#047857] inline-flex items-center gap-0.5"><MapPin size={12} weight="fill" /> {b.location}</span>}
                      {(b.check_in || b.check_out) && <span className="inline-flex items-center gap-1"><CalendarBlank size={12} /> {b.check_in}{b.check_out ? `–${b.check_out}` : ""}</span>}
                      {b.rooms ? <span>· {b.rooms} rooms</span> : null}
                      {b.room_nights ? <span>· {b.room_nights} rm-nts</span> : null}
                    </div>
                  </div>
                  <span className="font-display font-black text-[#111827] shrink-0">{money(b.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Deleted leads report */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-2">
          <Trash size={16} weight="bold" color="#B45309" />
          <span className="overline text-[#4B5563]">Deleted leads report ({deleted.length})</span>
        </div>
        {deleted.length === 0 ? (
          <p className="text-sm text-[#4B5563]" data-testid="deleted-leads-empty">No deleted leads. When a lead is deleted, it's archived here with who, when and why.</p>
        ) : (
          <div className="border border-[#E5E7EB] divide-y divide-[#E5E7EB]" data-testid="deleted-leads-list">
            {deleted.map((d) => (
              <div key={d.id} className="px-4 py-2.5 text-sm" data-testid={`deleted-lead-${d.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-[#111827]">{d.company_name || "Lead"} <span className="text-[#9CA3AF] font-normal">· {d.hotel_name || "—"} · was {d.status}</span></span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#9CA3AF]">Deleted by {d.deleted_by} · {(d.deleted_at || "").slice(0, 10)}</span>
                    <button data-testid={`restore-lead-${d.id}`} onClick={() => restore.mutate(d.id)} disabled={restore.isPending}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline disabled:opacity-60"><ArrowCounterClockwise size={13} weight="bold" /> Restore</button>
                  </div>
                </div>
                {d.delete_reason && <div className="text-xs text-[#6B7280] mt-0.5 italic">Reason: {d.delete_reason}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
